import { Component, OnDestroy, ViewChild, ViewChildren, ElementRef, QueryList } from '@angular/core';

import { Subscription, of } from 'rxjs';
import { filter, map, tap, catchError } from 'rxjs/operators';
import moment from 'moment-timezone';

import {
    MediaSubscription,
    TvEyesMediaTypes
} from '../../../Shared/models/media-subscription.model';
import {
    HandleHelper,
    twitterValidation,
    facebookValidation,
    instaValidation,
    youtubeValidation,
    redditValidation
} from '../../../../Common/Forms/helpers/handle.helper';
import { MediaSubscriptionService } from '../../../Core/services/media-subscription.service';
import { inputTypesEnum } from '../../../Shared/enums/input-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MediaSubscriptionErrorsModel } from '../../models/media-subscription-errors.model';
import { AttributeModel } from '../../../../Common/Misc/models/attribute.model';
import { mediaSubscriptionAttributeTypes } from '../../enums/media-subscription-attribute-types.enum';
import { attributeTypesEnum } from '../../../../Common/Misc/enums/attribute-types.enum';
import { MediaSubscriptionStatusEnum } from '@Monitor/Shared/enums/media-subscription-status.enum';
import { MediaSubscriptionMediaTypeEnum, mediaSubTypeToMediaTypeMap } from '../../../../api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { MediaSubscriptionTypeModel } from '../../models/media-subscription-type.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { MonitoringRoutingService } from '../../../Core/services/monitoring-routing.service';
import { FormMessageComponent } from '../../../../Common/Forms/components/FormMessage/form-message.component';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { InputValidationModel } from '../../../../Common/Forms/models/input-validation.model';
import { BackfillDialogComponent } from '../BackfillDialog/backfill-dialog.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { FocusHelper } from '../../../../Common/Forms/helpers/focus.helper';
import { SearchRangeModel } from '../../../Core/models/search-range.model';
import { BackfillStatusApiEnum } from '../../../../api/mediaSubscriptions/enums/backfill-status.api.enum';
import { NewResourceDialogModel } from '../../../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { DisplayGroupModel } from '../../../../Common/Layout/models/display-group.model';
import { ConfigService } from '@Core/Config/services/config.service';
import { SearchResultsService } from '../../../../Monitor/Core/services/search-results.service';
import { MonitoringViewModesEnum } from '@Monitor/Shared/enums/monitoring-view-modes.enum';
import { UserAppPreferencesModel } from '@Core/Users/models/user-app-preferences.model';
import { UserPreferencesService } from '@Account/UserPreferences/services/user-preferences.service';
import { ResourceActionTypes } from '@Common/Basic/enums/resource-actions.enum';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';
import { MediaRequestType } from '@api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';

enum FormModeEnum {
    KEYWORDS = 'KEYWORDS',
    HANDLES = 'HANDLES'
}

@Component({
    selector: 'agpr-media-subscription',
    templateUrl: './media-subscription.component.html',
    styleUrls: ['./media-subscription.component.scss'],
    standalone: false
})
export class MediaSubscriptionComponent implements OnDestroy {

    @ViewChild('scrollArea') scrollArea: ScrollPanelComponent;
    @ViewChildren(FormMessageComponent, {read: ElementRef}) formErrors: QueryList<ElementRef>;

    readonly id = 'mediaSubscription';
    readonly buttonTypes = ButtonTypesEnum;
    readonly tvEyesMediaTypes = TvEyesMediaTypes;
    readonly inputTypesEnum = inputTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly mediaTypesEnum = MediaSubscriptionMediaTypeEnum;
    readonly formModeEnum = FormModeEnum;
    readonly mediaSubscriptionStatusEnum = MediaSubscriptionStatusEnum;

    mediaSubscription: MediaSubscription = null;
    isDirty: boolean = false;

    mediaTypes: MediaSubscriptionTypeModel[] = MediaSubscriptionTypeModel.getAllowedMediaSubscriptionTypes();

    attributes: AttributeModel[] = [];
    backfillAttributes: AttributeModel[][] = [];

    showErrors: boolean = false;
    errors: MediaSubscriptionErrorsModel = new MediaSubscriptionErrorsModel();


    mediaSubscriptionsProvisioned: boolean = false;

    handlesName: string; // What "handles" are called for the given form mode.
    handlesLabel: string; // What label to give the "handles" field based on form mode.
    handlesPlaceholder: string; // What placeholder to show in the handles field based on form mode.

    formMode: FormModeEnum = FormModeEnum.KEYWORDS;

    handleValidators: InputValidationModel[] = [];

    disableActivateButton = false;
    activateButtonTooltip = '';

    financialPodcastOnlyEnabled: boolean = false;
    showFinancialTimesPodcastUpsell: boolean = false;
    userPrefs: UserAppPreferencesModel;

    private subscriptions: Subscription[] = [];

    constructor(
        public mediaSubscriptionService: MediaSubscriptionService,
        public searchResultsService: SearchResultsService,
        private coreDialogService: CoreDialogService,
        private monitoringRoutingService: MonitoringRoutingService,
        private monitorConfigService: MonitorConfigService,
        private el: ElementRef,
        private configService: ConfigService,
        private userPreferencesService: UserPreferencesService,
        private currentUserStateService: CurrentUserStateService
    ) {
        this.subscriptions.push(
            this.mediaSubscriptionService.currentResource$.subscribe(mediaSubscription => {
                this.mediaSubscription = mediaSubscription;

                this.updateHandlesLabel();
                this.setAttributes();
                this.setFormMode();

                if (this.mediaSubscription && this.mediaSubscription.id) {
                    this.isValid();
                } else if (this.mediaSubscription && !this.mediaSubscription.id) {
                    this.showErrors = true;
                    this.validateFormData();
                }
                this.getBackfillAttributes();
                this.setDefaultMediaType();
            }),
            this.mediaSubscriptionService.resourceDirty$.subscribe(isDirty => {
                this.isDirty = isDirty;
            }),
            this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.mediaSubscriptions]).subscribe(
                (provisioned) => {
                    this.mediaSubscriptionsProvisioned = provisioned;
                }
            ),
            this.monitorConfigService.getAvailableMediaTypes().pipe(
                filter(x => !!x)
            ).subscribe(
                (availableMediaTypes) => {
                    this.mediaTypes
                        .filter(x => x.id !== MediaSubscriptionMediaTypeEnum.broadcast) // Will be set by the call below
                        .forEach((mediaType) => {
                            mediaType.disabled = !availableMediaTypes.includes(
                                mediaSubTypeToMediaTypeMap.get(mediaType.id)
                            );
                        });

                    this.setDefaultMediaType();
                    this.updateHandlesLabel();
                    this.checkProvisionForMediaType();
                }
            ),
            this.monitorConfigService.getTvEyesEnabled().subscribe(enabled => {
                const broadcastType = this.mediaTypes.find(type => type.id === MediaSubscriptionMediaTypeEnum.broadcast);
                broadcastType.disabled = !enabled;
                broadcastType.hidden = !enabled;
                this.setDefaultMediaType();
                this.updateHandlesLabel();
                this.checkProvisionForMediaType();
            }),
            this.monitorConfigService.getFinancialPodcastOnlyEnabled().subscribe(enabled => {
                this.financialPodcastOnlyEnabled = enabled;
            }),
            this.userPreferencesService.getUserAppPreferences().subscribe(userAppPrefs => {
                this.userPrefs = userAppPrefs;
            }),
        );
        this.validateFormData();
    }

    scrollErrorsIntoView() {
        // Need an extra frame so angular has time to render the errors
        requestAnimationFrame(() => {
            if (this.formErrors) {
                this.scrollArea.scrollFirstIntoView(
                    this.formErrors.toArray()
                );
            }
        });
    }

    updateHandlesLabel() {
        if (this.mediaSubscription && this.mediaSubscription.type) {
            this.handleValidators = [];

            switch (this.mediaSubscription.type) {
                case MediaSubscriptionMediaTypeEnum.facebook:
                    this.handlesName = 'Monitoring.mediaSubscriptions.handles';
                    this.handlesLabel = 'MediaSubscriptions.facebookPagesDescription';
                    this.handlesPlaceholder = 'MediaSubscriptions.facebookPlaceholder';
                    this.handleValidators.push(facebookValidation);
                    break;
                case MediaSubscriptionMediaTypeEnum.twitter:
                    this.handlesName = 'Monitoring.mediaSubscriptions.handles';
                    this.handlesLabel = 'MediaSubscriptions.twitterHandlesDescription';
                    this.handlesPlaceholder = 'MediaSubscriptions.twitterPlaceholder';
                    this.handleValidators.push(twitterValidation);
                    break;
                case MediaSubscriptionMediaTypeEnum.instagram:
                    this.handlesName = 'Monitoring.mediaSubscriptions.handles';
                    this.handlesLabel = 'MediaSubscriptions.instagramHandlesDescription';
                    this.handlesPlaceholder = 'MediaSubscriptions.instagramPlaceholder';
                    this.handleValidators.push(instaValidation);
                    break;
                case MediaSubscriptionMediaTypeEnum.reddit:
                    this.handlesName = 'Monitoring.mediaSubscriptions.userProfiles';
                    this.handlesLabel = 'MediaSubscriptions.redditHandlesDescription';
                    this.handlesPlaceholder = 'MediaSubscriptions.redditPlaceholder';
                    this.handleValidators.push(redditValidation);
                    break;
                case MediaSubscriptionMediaTypeEnum.youtube:
                    this.handlesName = 'Monitoring.mediaSubscriptions.channels';
                    this.handlesLabel = 'MediaSubscriptions.youtubeHandlesDescription';
                    this.handlesPlaceholder = 'MediaSubscriptions.youtubePlaceholder';
                    this.handleValidators.push(youtubeValidation);
                    break;
            }
        }
    }

    /**
     * The different media types have different form setups. This function determines which form setup to use.
     */
    public setFormMode() {
        // The Handles field is hidden for TV/Radio (broadcast) and Podcast
        const hasNoHandlesField = this.mediaSubscription?.type === MediaSubscriptionMediaTypeEnum.broadcast
            || this.mediaSubscription?.type === MediaSubscriptionMediaTypeEnum.podcast;
        const hasNoHandles = this.mediaSubscription?.keywords?.handles?.length === 0;

        this.formMode = hasNoHandlesField || hasNoHandles ? FormModeEnum.KEYWORDS : FormModeEnum.HANDLES;

        this.checkProvisionForMediaType();
    }

    /** Triggered on change of keywords.handles, type and tvEyesMediaType */
    updateMediaTypeRequest() {
        if (this.mediaSubscription.type === MediaSubscriptionMediaTypeEnum.broadcast) {
            this.mediaSubscription.mediaTypeRequests = [];

            if ([TvEyesMediaTypes.all, TvEyesMediaTypes.tv].includes(this.mediaSubscription.tvEyesMediaType)) {
                this.mediaSubscription.mediaTypeRequests.push({
                    mediaType: MediaTypeEnum.TV,
                    handles: [],
                    isModeratedForKeywords: false,
                    isModeratedForHandles: false,
                    blacklistedHandles: null
                });
            }

            if ([TvEyesMediaTypes.all, TvEyesMediaTypes.radio].includes(this.mediaSubscription.tvEyesMediaType)) {
                this.mediaSubscription.mediaTypeRequests.push({
                    mediaType: MediaTypeEnum.RADIO,
                    handles: [],
                    isModeratedForKeywords: false,
                    isModeratedForHandles: false,
                    blacklistedHandles: null
                });
            }
        } else {
            // values are the same other than broadcast
            this.mediaSubscription.mediaTypeRequests = [{
                mediaType: this.mediaSubscription.type as MediaRequestType,
                handles: Object.assign([], this.mediaSubscription.keywords.handles),
                isModeratedForKeywords: false,
                isModeratedForHandles: false,
                blacklistedHandles: null
            }];
        }
    }

    /**
     * Checks to see if the media subscription
     * has been updated since it was loaded
     */
    checkForUpdates(): void {
        this.performModeSwitch();
        this.validateFormData();

        this.showFinancialTimesPodcastUpsell = this.financialPodcastOnlyEnabled && this.mediaSubscription.type === MediaSubscriptionMediaTypeEnum.podcast;
    }

    setKeywords(property: string, keywords: string[]) {
        this.mediaSubscription.keywords[property] = keywords.slice();

        this.checkForUpdates();
        this.onSubscriptionChange();
    }

    // When you switch to handles, makes the keywords go away and vice versa
    performModeSwitch() {
        const confirmDialogInputs = {
            id: `${this.id}_dataLoss`,
            heading: 'MediaSubscriptions.dataloss.dialog.header',
            messageHtml: 'MediaSubscriptions.dataloss.dialog.message',
            okLabel: 'Monitoring.confirm'
        };

        if (this.formMode === FormModeEnum.HANDLES) {

            if (this.mediaSubscription.keywords.hasAnyKeywords()) {
                const confirmConfig: ConfirmDialogModel = {
                    inputs: confirmDialogInputs,
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            if (confirmed) {
                                this.mediaSubscription.keywords.clearAllKeywords();
                            } else {
                                this.formMode = FormModeEnum.KEYWORDS;
                            }
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
            }
        } else {
            if (this.mediaSubscription.keywords.handles.length > 0) {
                const confirmConfig: ConfirmDialogModel = {
                    inputs: confirmDialogInputs,
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            if (confirmed) {
                                this.mediaSubscription.keywords.handles.length = 0;
                            } else {
                                this.formMode = FormModeEnum.HANDLES;
                            }
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
            }
        }
    }

    saveButtonState(): boolean {
        const keywordsInputsState =
            this.mediaSubscription.keywords.or.length === 0 &&
            this.mediaSubscription.keywords.not.length === 0 &&
            this.mediaSubscription.keywords.and.length === 0;

        const handlesInputState = this.mediaSubscription.keywords.handles.length === 0;

        if (this.mediaSubscription.type !== this.mediaTypesEnum.broadcast && this.mediaSubscription.type !== this.mediaTypesEnum.podcast) {
            return keywordsInputsState && handlesInputState;
        } else {
            return keywordsInputsState;
        }
    }


    /**
     * Sets the secondary attributes
     */
    private setAttributes() {
        if (!this.mediaSubscription) {
            this.attributes = [];
            return;
        }

        const createdByAttribute: AttributeModel = {
            id: mediaSubscriptionAttributeTypes.createdBy,
            label: 'createdBy',
            value: this.mediaSubscription.createdByUserId,
            type: attributeTypesEnum.user
        };

        const createdDateAttribute: AttributeModel = {
            id: mediaSubscriptionAttributeTypes.createdDate,
            label: 'dateCreated',
            value: this.mediaSubscription.createdDate,
            type: attributeTypesEnum.date
        };

        const modifiedByAttribute: AttributeModel = {
            id: mediaSubscriptionAttributeTypes.modifiedBy,
            label: 'lastModifiedBy',
            value: this.mediaSubscription.modifiedByUserId,
            type: attributeTypesEnum.user
        };

        const modifiedDateAttribute: AttributeModel = {
            id: mediaSubscriptionAttributeTypes.modifiedDate,
            label: 'lastModifiedDate',
            value: this.mediaSubscription.modifiedDate,
            type: attributeTypesEnum.date
        };

        this.attributes = [
            createdByAttribute,
            createdDateAttribute,
            modifiedByAttribute,
            modifiedDateAttribute
        ].filter(x => !!x.value);
    }


    onSubscriptionChange() {
        if (!this.mediaSubscription) {
            return;
        }

        this.updateMediaTypeRequest();

        this.mediaSubscriptionService.currentResourceModified();
    }


    /**
     * Set the media type to the first option that is not disabled if no media type available
     */
    setDefaultMediaType(): void {
        const isEmpty = this.mediaSubscription &&
                        !this.mediaSubscription.id &&
                        !this.mediaSubscription?.keywords?.and.length &&
                        !this.mediaSubscription?.keywords?.or.length  &&
                        !this.mediaSubscription?.keywords?.not.length &&
                        !this.mediaSubscription?.keywords?.handles.length;

        if (isEmpty) {
            this.mediaSubscription.type = this.mediaTypes.find((mediaType) => {
                return !mediaType.disabled;
            })?.id;
        }
    }

    /**
     * Validate the subscription fields, then prompt the user for a name and save if not blank or duplicate
     */
    save() {
        this.showErrors = true;
        if (!this.validateFormData()) {
            FocusHelper.focusFirstError(this.el.nativeElement);
            this.scrollErrorsIntoView();

            return;
        }

        const io: NewResourceDialogModel = {
            inputs: {
                width: 300,
                heading: 'Monitoring.mediaSubscriptions.saveMediaSubscriptionHeader',
                description: 'Monitoring.mediaSubscriptions.saveMediaSubscriptionMessage',
                placeholder: 'Monitoring.mediaSubscriptions.saveMediaSubscriptionHint',
                currentGroup: this.mediaSubscription.groupId,
                observableFormValidator: (subName: string) => {
                    return this.mediaSubscriptionService.isNameDuplicated$(subName).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['MediaSubscriptions.saveMediaSubscriptionsDuplicateNameErrorMessage'];
                            } else {
                                return [];
                            }
                        })
                    );
                },
                submit: (name: string, group?: DisplayGroupModel) => {
                    this.mediaSubscription.name = name;
                    this.mediaSubscription.status = MediaSubscriptionStatusEnum.PAUSED;
                    this.mediaSubscription.groupId = group?.id;

                    this.removeUnusedSearchTerms(this.mediaSubscription);

                    return this.mediaSubscriptionService.create(this.mediaSubscription, group).pipe(
                        tap((id) => this.monitoringRoutingService.gotoUpdateMediaSubscription(id))
                    );
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, io);

    }

    /**
     * Updates existing subscription
     */
    update() {
        this.showErrors = true;
        if (!this.isValid()) {
            this.scrollErrorsIntoView();

            return;
        }

        this.removeUnusedSearchTerms(this.mediaSubscription);

        this.mediaSubscriptionService.update(this.mediaSubscription, ResourceActionTypes.UPDATE).subscribe();
    }


    /**
     * Activates the current subscription
     */
    activate(): void {
        this.showErrors = true;
        if (!this.isValid()) {
            this.scrollErrorsIntoView();

            return;
        }

        this.mediaSubscriptionService.activateCurrentSubscription().subscribe();
    }

    pause() {
        this.mediaSubscriptionService.pauseCurrentSubscription().subscribe();
    }

    /**
     * Navigate to the results page
     */
    viewMentions() {
        const defaultView = this.userPrefs.monitoringPrefs.subscriptionsPrefs?.defaultDisplaySettings?.resultsViewMode
        ?? this.searchResultsService.getCurrentViewMode();
        if (defaultView === MonitoringViewModesEnum.MENTION) {
            this.monitoringRoutingService.gotoMediaSubscriptionResults(this.mediaSubscription.id);
        } else {
            this.monitoringRoutingService.gotoSubscriptionAnalyticsDashboard(this.mediaSubscription.id);
        }
    }

    /**
     * Checks if the name is filled out, and toggles an error
     * based on the response
     *
     * @returns TRUE if validation passes, false otherwise
     */
    validateName(): boolean {
        // Reset only is for allowing the user to clear errors
        // without being bothered by new ones
        if (this.mediaSubscription && this.mediaSubscription.hasName()) {
            this.errors.emptySubscriptionName = false;
            this.checkForUpdates();
        } else {
            this.errors.emptySubscriptionName = true;
        }

        return !this.errors.emptySubscriptionName;
    }

    /**
     * Checks if there are any include keywords (AND, OR, Handles)
     * and toggles and error based on the response
     *
     * @returns TRUE if validation passes, false otherwise
     */
    private validateFormData(): boolean {

        if (this.mediaSubscription && this.mediaSubscription.type) {
            const terms = [];
            if (this.formMode === FormModeEnum.KEYWORDS) {
                this.errors.noHandles = false;
                this.errors.noIncludeKeywords = !this.mediaSubscription.keywords.hasIncludeKeywords();
                terms.push(
                    ...this.mediaSubscription.keywords.allIncludeKeywords,
                    ...this.mediaSubscription.keywords.not
                );
            } else {

                this.errors.noIncludeKeywords = false;
                this.errors.noHandles = this.mediaSubscription.keywords.handles.length < 1;
                terms.push(...this.mediaSubscription.keywords.handles);
            }

            switch (this.mediaSubscription.type) {
                case MediaSubscriptionMediaTypeEnum.facebook:
                case MediaSubscriptionMediaTypeEnum.twitter:
                case MediaSubscriptionMediaTypeEnum.instagram:
                    this.errors.tooManyTerms = terms.length > 30;
                    break;
                default:
                    this.errors.tooManyTerms = false;
            }

            const allKeywords = this.mediaSubscription.keywords.and
                .concat(this.mediaSubscription.keywords.or)
                .concat(this.mediaSubscription.keywords.not);

            this.errors.hashtagHasPunctuation = false;
            this.errors.duplicateKeywords = false;
            allKeywords.forEach((keyword) => {
                // if keyword is a hashtag then check for punctuation
                if (keyword.charAt(0) === '#') {
                    const substr = keyword.slice(1, keyword.length);
                    if (substr.match(/[\!\"\#\$\%\&\'\(\)\*\+\,\-\.\/:;<=>?@\[\\\]\^_`{\|}~]/)) {
                        this.errors.hashtagHasPunctuation = true;
                    }
                } else if (this.mediaSubscription.keywords.not.includes(keyword)){
                    const includedKeywords = this.mediaSubscription.keywords.and.concat(this.mediaSubscription.keywords.or);

                    if (includedKeywords.includes(keyword)){
                        this.errors.duplicateKeywords = true;
                    }
                }
            });

            return (
                !this.errors.noHandles &&
                !this.errors.noIncludeKeywords &&
                !this.errors.tooManyTerms &&
                !this.errors.hashtagHasPunctuation &&
                !this.errors.duplicateKeywords
            );
        }

        return false;
    }

    /**
     * Determines whether or not all required fields are populated
     */
    isValid(): boolean {
        this.validateName();
        this.validateFormData();

        return !this.errors.emptySubscriptionName
            && !this.errors.noIncludeKeywords
            && !this.errors.duplicateKeywords
            && !this.errors.noHandles
            && !this.errors.tooManyTerms
            && !this.errors.hashtagHasPunctuation;
    }



    /**
     * Removes any search terms from the disabled portion of the form.
     */
    private removeUnusedSearchTerms(mediaSubscription: MediaSubscription) {
        if (this.formMode === FormModeEnum.HANDLES) {
            mediaSubscription.keywords.clearAllKeywords();
        } else {
            mediaSubscription.keywords.handles = [];
        }
    }

    backfill() {
        const allowedRange = new SearchRangeModel();
        allowedRange.to = new Date();
        allowedRange.from = moment().subtract(90, 'days').toDate();

        this.coreDialogService.open(BackfillDialogComponent, {
            inputs: {
                subscription: this.mediaSubscription,
                allowedRange: allowedRange
            },
            outputs: {
                dialogClose: (requestSent: boolean) => {
                    if (requestSent) {
                        this.refresh();
                    }
                }
            }
        });
    }

    getBackfillAttributes() {
        if (!this.mediaSubscription || !this.mediaSubscription.backfills) {
            this.backfillAttributes = [];
            return;
        }
        const backfills = this.mediaSubscription.backfills.slice();
        backfills.sort((a, b) => +b.createdDate - +a.createdDate);

        this.backfillAttributes = backfills.map(
            (backfill, index) => {
                return [
                    {
                        id: `backfill.${index}.requestedDate`,
                        label: 'MediaSubscriptions.backfill.dateRequested',
                        value: backfill.createdDate,
                        type: attributeTypesEnum.date,
                        icon: backfill.status === BackfillStatusApiEnum.completed ?
                            'fa-regular fa-duotone fa-duotone fa-circle-check' : 'fa-kit fa-agpr-in-progress',
                        iconToolTip:  backfill.status === BackfillStatusApiEnum.completed
                            ? 'Monitoring.search.backfill.status.completed'
                            : 'Monitoring.search.backfill.status.inProgress'
                    },
                    {
                        id: `backfill.${index}.fromDate`,
                        label: 'MediaSubscriptions.backfill.fromDate',
                        value: backfill.fromDate,
                        type: attributeTypesEnum.date
                    },
                    {
                        id: `backfill.${index}.toDate`,
                        label: 'MediaSubscriptions.backfill.toDate',
                        value: backfill.toDate,
                        type: attributeTypesEnum.date
                    },
                    {
                        id: `backfill.${index}.mentionCount`,
                        label: 'MediaSubscriptions.backfill.mentions',
                        value: backfill.mentionCount,
                        type: attributeTypesEnum.number
                    }
                ];
            }
        );
    }

    checkProvisionForMediaType() {
        for (const type of this.mediaTypes) {
            if (
                (type.disabled || (type.id === MediaSubscriptionMediaTypeEnum.broadcast) || (type.id === MediaSubscriptionMediaTypeEnum.podcast)) &&
                type.id === this.mediaSubscription?.type
            ) {
                this.disableActivateButton = true;
                this.activateButtonTooltip = 'MediaSubscriptions.activate.disabled';
                return;
            } else {
                this.disableActivateButton = false;
                this.activateButtonTooltip = '';
            }
        }
    }

    refresh() {
        if (this.mediaSubscription) {
            this.mediaSubscriptionService.getById(this.mediaSubscription.id).pipe(
                catchError(() => {
                    this.monitoringRoutingService.gotoSubscriptionHype();
                    return of(null);
                }),
                tap(subscription => this.mediaSubscriptionService.setCurrentResource(subscription))
            ).subscribe();
        }
    }

    upsellPodcasts() {
        if (this.configService.brandGUIDIsBurrelles()) {
            window.location.href = 'mailto:inquiry@burrelles.com?subject=Sales%20Inquiry%20-%20Burrelles%20Contact%20Database';
        } else {
            window.open('https://www.agilitypr.com/contact/');
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
